import React, { useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "../../lib/format";

function brandWordmark(ctx, x, y, size = 46) {
  ctx.font = `700 ${size}px Inter, Arial, sans-serif`;
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#ffffff";
  const brand = "COMMITMEN";
  ctx.fillText(brand, x, y);
  const bw = ctx.measureText(brand).width;
  ctx.fillStyle = "#10B981";
  ctx.fillText("TT", x + bw, y);
}

function drawSafe(position) {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0f2a43"; ctx.fillRect(0, 0, W, H);
  ctx.beginPath(); ctx.fillStyle = "rgba(16,185,129,0.10)"; ctx.arc(W - 120, 150, 320, 0, Math.PI * 2); ctx.fill();
  brandWordmark(ctx, 90, 160);
  ctx.fillStyle = "rgba(255,255,255,0.7)"; ctx.font = "400 40px Inter, Arial, sans-serif";
  ctx.fillText(position.safe_label || "Estimated Safe to Spend", 90, 560);
  ctx.fillStyle = "#10B981"; ctx.font = "300 150px Inter, Arial, sans-serif";
  ctx.fillText(formatINR(position.safe_to_spend), 90, 700);
  const stats = [
    ["Monthly income", formatINR(position.monthly_income)],
    ["Commitment ratio", `${position.commitment_ratio}%`],
    ["Committed this month", formatINR(position.committed_this_month)],
  ];
  let y = 850;
  stats.forEach(([l, v]) => {
    ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.font = "400 36px Inter, Arial, sans-serif"; ctx.fillText(l, 90, y);
    ctx.fillStyle = "#ffffff"; ctx.font = "600 40px Inter, Arial, sans-serif";
    const vw = ctx.measureText(v).width; ctx.fillText(v, W - 90 - vw, y);
    ctx.strokeStyle = "rgba(255,255,255,0.12)"; ctx.beginPath(); ctx.moveTo(90, y + 30); ctx.lineTo(W - 90, y + 30); ctx.stroke();
    y += 110;
  });
  ctx.fillStyle = "rgba(255,255,255,0.4)"; ctx.font = "400 26px Inter, Arial, sans-serif";
  ctx.fillText("An estimate based on what you track — not a live bank balance.", 90, H - 90);
  return canvas;
}

function drawStreak() {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0f2a43"; ctx.fillRect(0, 0, W, H);
  ctx.beginPath(); ctx.fillStyle = "rgba(16,185,129,0.10)"; ctx.arc(150, H - 120, 360, 0, Math.PI * 2); ctx.fill();
  brandWordmark(ctx, 90, 160);

  // Check medallion
  const cx = W / 2, cy = 560, r = 150;
  ctx.beginPath(); ctx.fillStyle = "rgba(16,185,129,0.15)"; ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.strokeStyle = "#10B981"; ctx.lineWidth = 14; ctx.lineCap = "round";
  ctx.moveTo(cx - 62, cy + 4); ctx.lineTo(cx - 14, cy + 52); ctx.lineTo(cx + 70, cy - 52); ctx.stroke();

  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff"; ctx.font = "300 88px Inter, Arial, sans-serif";
  ctx.fillText("All caught up", cx, cy + 300);
  ctx.fillStyle = "#10B981"; ctx.font = "600 64px Inter, Arial, sans-serif";
  ctx.fillText("this week", cx, cy + 380);

  ctx.fillStyle = "rgba(255,255,255,0.75)"; ctx.font = "400 40px Inter, Arial, sans-serif";
  ctx.fillText("Every payment due this week is paid.", cx, cy + 480);
  ctx.fillText("Nothing else needs my attention right now.", cx, cy + 540);

  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(255,255,255,0.4)"; ctx.font = "400 26px Inter, Arial, sans-serif";
  ctx.fillText("Tracked with COMMITMENTT — know what you've committed, stay ahead.", 90, H - 90);
  return canvas;
}

export default function ShareSnapshot({ position, variant = "safe", className = "" }) {
  const [busy, setBusy] = useState(false);
  if (variant === "safe" && !position) return null;

  const share = async () => {
    setBusy(true);
    try {
      const canvas = variant === "streak" ? drawStreak() : drawSafe(position);
      const fileName = variant === "streak" ? "commitmentt-all-caught-up.png" : "commitmentt-safe-to-spend.png";
      const shareText = variant === "streak"
        ? "All caught up this week 🎉 — via COMMITMENTT."
        : "My Safe to Spend, via COMMITMENTT.";
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "COMMITMENTT", text: shareText });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = fileName;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Snapshot saved to your device.");
      }
    } catch (e) {
      if (e && e.name !== "AbortError") toast.error("Couldn't create the snapshot.");
    } finally {
      setBusy(false);
    }
  };

  if (variant === "streak") {
    return (
      <button
        onClick={share}
        disabled={busy}
        data-testid="share-streak-btn"
        className={`inline-flex items-center gap-1.5 text-sm font-medium text-calm-success hover:underline disabled:opacity-50 ${className}`}
      >
        <Share2 size={15} strokeWidth={1.5} />Share this
      </button>
    );
  }

  return (
    <button
      onClick={share}
      disabled={busy}
      data-testid="share-snapshot-btn"
      aria-label="Share Safe to Spend snapshot"
      className={`h-8 w-8 rounded-full flex items-center justify-center text-slatey hover:bg-secondary transition-colors disabled:opacity-50 ${className}`}
    >
      <Share2 size={16} strokeWidth={1.5} />
    </button>
  );
}
